import { useState, useMemo, useEffect } from 'react'
import { ProductFilter } from 'baby-ui/lib/ProductFilter'
import ProductSorter from 'baby-ui/lib/ProductSorter'
import { SortOrder } from 'baby-ui/lib/SortOrder'
import { BRAND_INDEX, FEATURES_INDEX } from 'baby-ui/shared/constants'
import {
  PRICE_SORT_PATH,
  SORT_OPTIONS,
} from '../../dme/widgets/EnhancedProductFilterWidget/EnhancedProductFilterWidget.constants'

type Product = APIResponse.HeroProduct

export interface ProductFilterControls {
  applyFilter: (matrix: string[][]) => void
  applySort: (criteria: keyof typeof SORT_OPTIONS) => void
  allProducts: Product[]
}

export type ProductFilterHook = [Product[], ProductFilterControls]

export const useProductFilter = (rawProducts: Product[]) => {
  const [products, setProducts] = useState(rawProducts)
  const filter = useMemo(
    () => new ProductFilter(rawProducts, [BRAND_INDEX, FEATURES_INDEX]),
    [rawProducts.length]
  )

  const [sortPath, setSortPath] = useState(PRICE_SORT_PATH)
  const [sortOrder, setSortOrder] = useState(SortOrder.Asc)
  const [filterMatrix, setFilterMatrix] = useState<string[][]>([])

  useEffect(() => {
    if (rawProducts.length) {
      const filteredProducts = getFilteredProducts(filterMatrix)
      const sortedProducts = getSortedProducts(
        filteredProducts,
        sortPath,
        sortOrder
      )
      setProducts(sortedProducts)
    }
  }, [
    JSON.stringify(filterMatrix),
    filter.products.length,
    sortOrder,
    sortPath,
  ])

  const getSortedProducts = (
    products: Product[],
    by: string,
    order: SortOrder
  ) => new ProductSorter(products).sort({ by, order })

  const getFilteredProducts = (filterMatrix: string[][]) => {
    const shouldFilter = filterMatrix && filterMatrix.length > 0
    return shouldFilter ? filter.intersection(filterMatrix) : filter.products
  }

  const applyFilter = (matrix: string[][]) => {
    setFilterMatrix(matrix)
  }

  const applySort = (criteria: keyof typeof SORT_OPTIONS) => {
    const { path, direction } = SORT_OPTIONS[criteria]
    setSortOrder(direction)
    setSortPath(path)
  }

  return [
    products,
    { allProducts: rawProducts, applyFilter, applySort },
  ] as ProductFilterHook
}
